// Katalog pengaturan usaha — satu sumber untuk server (validasi) & layar Pengaturan (form dirender dari sini).
// Nilai tersimpan di tabel settings: saat pemasangan setiap kunci ditulis sekali dengan nilai awalnya, setelah itu
// database yang berlaku. Daftar pilihan (satuan, kategori, alasan, rekening, …) ada di Data Master (master.ts).

export type SettingType = 'text' | 'textarea' | 'number' | 'select';

export interface SettingDef {
  key: string;
  group: string;
  label: string;
  type: SettingType;
  /** Nilai awal saat pemasangan. */
  def: string;
  hint?: string;
  min?: number;
  max?: number;
  /** Boleh desimal (koordinat). */
  decimal?: boolean;
  maxLength?: number;
  /** Pilihan untuk type 'select'. */
  options?: readonly { value: string; label: string }[];
  /** Pola wajib (regex) + penjelasannya. */
  pattern?: string;
  patternMsg?: string;
  span?: boolean;
}

export const SETTINGS: readonly SettingDef[] = [
  // ---- Data usaha (tercetak di nota & dokumen) ----
  { key: 'business_name', group: 'Data usaha', label: 'Nama usaha', type: 'text', def: '', maxLength: 120 },
  { key: 'phone', group: 'Data usaha', label: 'Telepon kantor', type: 'text', def: '', maxLength: 60 },
  {
    key: 'phone_note',
    group: 'Data usaha',
    label: 'Keterangan telepon',
    type: 'text',
    def: '',
    maxLength: 60,
    hint: 'mis. Tilpun & Fax',
  },
  { key: 'address', group: 'Data usaha', label: 'Alamat (baris 1)', type: 'text', def: '', maxLength: 300, span: true },
  { key: 'address2', group: 'Data usaha', label: 'Alamat (baris 2)', type: 'text', def: '', maxLength: 300, span: true },
  // ---- Lokasi gudang/kantor (pusat peta, titik berangkat) ----
  {
    key: 'depot_lat',
    group: 'Lokasi gudang / kantor',
    label: 'Latitude',
    type: 'number',
    def: '',
    min: -90,
    max: 90,
    decimal: true,
    hint: 'Pusat peta bila belum ada titik toko/driver.',
  },
  {
    key: 'depot_lng',
    group: 'Lokasi gudang / kantor',
    label: 'Longitude',
    type: 'number',
    def: '',
    min: -180,
    max: 180,
    decimal: true,
  },
  // ---- Penjualan ----
  {
    key: 'default_term_days',
    group: 'Penjualan',
    label: 'Termin tempo bawaan (hari)',
    type: 'number',
    def: '7',
    min: 1,
    max: 365,
    hint: 'Dipakai bila toko belum punya termin sendiri.',
  },
  // ---- Aturan kunjungan sales (anti kunjungan fiktif) ----
  {
    key: 'visit_radius_m',
    group: 'Aturan kunjungan sales',
    label: 'Radius check-in (meter)',
    type: 'number',
    def: '150',
    min: 20,
    max: 2000,
    hint: 'Sales harus sedekat ini dengan titik toko.',
  },
  {
    key: 'visit_gps_acc_max_m',
    group: 'Aturan kunjungan sales',
    label: 'Batas akurasi GPS (meter)',
    type: 'number',
    def: '100',
    min: 10,
    max: 1000,
    hint: 'Lebih dari ini → ditandai "GPS lemah".',
  },
  {
    key: 'visit_fast_kmh',
    group: 'Aturan kunjungan sales',
    label: 'Batas kecepatan antar toko (km/jam)',
    type: 'number',
    def: '80',
    min: 20,
    max: 300,
    hint: 'Lebih cepat → ditandai "pindah toko terlalu cepat".',
  },
  {
    key: 'visit_confirm_days',
    group: 'Aturan kunjungan sales',
    label: 'Batas konfirmasi kunjungan oleh toko (hari)',
    type: 'number',
    def: '7',
    min: 1,
    max: 60,
  },
  {
    key: 'visit_request_days',
    group: 'Aturan kunjungan sales',
    label: 'Masa berlaku permintaan kunjungan (hari)',
    type: 'number',
    def: '14',
    min: 1,
    max: 60,
  },
  {
    key: 'visit_check_days',
    group: 'Aturan kunjungan sales',
    label: 'Rentang daftar "Perlu dicek" (hari)',
    type: 'number',
    def: '30',
    min: 1,
    max: 365,
  },
  // ---- Data & privasi ----
  {
    key: 'gps_retention_days',
    group: 'Data & privasi',
    label: 'Simpan jejak GPS (hari)',
    type: 'number',
    def: '90',
    min: 7,
    max: 3650,
    hint: 'Titik GPS lebih lama dari ini dihapus otomatis.',
  },
  // ---- Ambang & perhitungan ----
  {
    key: 'driver_arrive_m',
    group: 'Ambang & perhitungan',
    label: 'Radius driver dianggap tiba di toko (meter)',
    type: 'number',
    def: '60',
    min: 10,
    max: 1000,
  },
  {
    key: 'nearest_store_m',
    group: 'Ambang & perhitungan',
    label: 'Jarak penanda "toko terdekat" di SPO Baru (meter)',
    type: 'number',
    def: '300',
    min: 20,
    max: 5000,
  },
  {
    key: 'aging_days_1',
    group: 'Ambang & perhitungan',
    label: 'Umur piutang — batas kelompok pertama (hari)',
    type: 'number',
    def: '30',
    min: 1,
    max: 365,
  },
  {
    key: 'aging_days_2',
    group: 'Ambang & perhitungan',
    label: 'Umur piutang — batas kelompok kedua (hari)',
    type: 'number',
    def: '60',
    min: 2,
    max: 730,
    hint: 'Harus lebih besar dari batas pertama.',
  },
  {
    key: 'eta_speed_kmh',
    group: 'Ambang & perhitungan',
    label: 'Kecepatan rata-rata kendaraan untuk perkiraan tiba (km/jam)',
    type: 'number',
    def: '25',
    min: 5,
    max: 120,
  },
  {
    key: 'eta_detour_factor',
    group: 'Ambang & perhitungan',
    label: 'Faktor jalan berliku (jarak jalan ÷ garis lurus)',
    type: 'number',
    def: '1.35',
    min: 1,
    max: 3,
    decimal: true,
    hint: 'Dipakai bila layanan rute tidak tersedia.',
  },
  // ---- Penomoran dokumen (awalan; nomor = AWALAN-YYMM-NNNN) ----
  {
    key: 'prefix_spo',
    group: 'Penomoran dokumen',
    label: 'Awalan nomor SPO',
    type: 'text',
    def: 'SPO',
    maxLength: 6,
    pattern: '^[A-Z0-9]{1,6}$',
    patternMsg: 'huruf besar/angka, 1–6 karakter',
  },
  {
    key: 'prefix_nota',
    group: 'Penomoran dokumen',
    label: 'Awalan nomor nota',
    type: 'text',
    def: 'NJ',
    maxLength: 6,
    pattern: '^[A-Z0-9]{1,6}$',
    patternMsg: 'huruf besar/angka, 1–6 karakter',
  },
  {
    key: 'prefix_route',
    group: 'Penomoran dokumen',
    label: 'Awalan nomor rute kirim',
    type: 'text',
    def: 'RK',
    maxLength: 6,
    pattern: '^[A-Z0-9]{1,6}$',
    patternMsg: 'huruf besar/angka, 1–6 karakter',
  },
  {
    key: 'prefix_deposit',
    group: 'Penomoran dokumen',
    label: 'Awalan nomor setoran',
    type: 'text',
    def: 'STR',
    maxLength: 6,
    pattern: '^[A-Z0-9]{1,6}$',
    patternMsg: 'huruf besar/angka, 1–6 karakter',
  },
  {
    key: 'prefix_stock_in',
    group: 'Penomoran dokumen',
    label: 'Awalan nomor barang masuk',
    type: 'text',
    def: 'MSK',
    maxLength: 6,
    pattern: '^[A-Z0-9]{1,6}$',
    patternMsg: 'huruf besar/angka, 1–6 karakter',
  },
  {
    key: 'prefix_adjust',
    group: 'Penomoran dokumen',
    label: 'Awalan nomor penyesuaian stok',
    type: 'text',
    def: 'ADJ',
    maxLength: 6,
    pattern: '^[A-Z0-9]{1,6}$',
    patternMsg: 'huruf besar/angka, 1–6 karakter',
  },
  {
    key: 'prefix_store',
    group: 'Penomoran dokumen',
    label: 'Awalan kode toko otomatis',
    type: 'text',
    def: 'TK',
    maxLength: 6,
    pattern: '^[A-Z0-9]{1,6}$',
    patternMsg: 'huruf besar/angka, 1–6 karakter',
  },
  // ---- Cetak nota (kertas F4 dibagi 3 atau 2) ----
  {
    key: 'nota_paper',
    group: 'Cetak nota',
    label: 'Ukuran kertas nota',
    type: 'select',
    def: 'auto',
    options: [
      { value: 'auto', label: 'Otomatis (1/3 F4 bila item sedikit, 1/2 F4 bila banyak)' },
      { value: 'f4_3', label: '1/3 F4 (215 × 110 mm)' },
      { value: 'f4_2', label: '1/2 F4 (215 × 165 mm)' },
    ],
  },
  {
    key: 'nota_third_max_items',
    group: 'Cetak nota',
    label: 'Maksimal baris barang untuk 1/3 F4 (mode otomatis)',
    type: 'number',
    def: '5',
    min: 1,
    max: 20,
    hint: 'Lebih dari ini → dicetak 1/2 F4; nota yang sangat panjang berlanjut ke lembar berikutnya.',
  },
  {
    key: 'nota_footer',
    group: 'Cetak nota',
    label: 'Catatan kaki nota',
    type: 'textarea',
    def: '',
    maxLength: 1000,
    span: true,
    hint: 'Satu catatan per baris — tercetak di bawah nota.',
  },
  // ---- Label dokumen cetak ----
  {
    key: 'doc_title_cash',
    group: 'Label dokumen cetak',
    label: 'Judul nota cash',
    type: 'text',
    def: 'NOTA KONTAN',
    maxLength: 60,
  },
  {
    key: 'doc_title_tempo',
    group: 'Label dokumen cetak',
    label: 'Judul nota tempo/transfer',
    type: 'text',
    def: 'NOTA PENJUALAN',
    maxLength: 60,
  },
  {
    key: 'doc_col_product',
    group: 'Label dokumen cetak',
    label: 'Judul kolom produk di nota',
    type: 'text',
    def: 'Product Flavour',
    maxLength: 60,
  },
  {
    key: 'doc_office_label',
    group: 'Label dokumen cetak',
    label: 'Label telepon kantor di nota',
    type: 'text',
    def: 'Tilp Office',
    maxLength: 60,
  },
  {
    key: 'doc_sign_seller',
    group: 'Label dokumen cetak',
    label: 'TTD penjual (judul)',
    type: 'text',
    def: 'Hormat Kami',
    maxLength: 60,
  },
  {
    key: 'doc_sign_seller_sub',
    group: 'Label dokumen cetak',
    label: 'TTD penjual (keterangan)',
    type: 'text',
    def: 'Administrasi',
    maxLength: 60,
  },
  {
    key: 'doc_sign_buyer',
    group: 'Label dokumen cetak',
    label: 'TTD pembeli (judul)',
    type: 'text',
    def: 'Pembeli',
    maxLength: 60,
  },
  {
    key: 'doc_sign_buyer_sub',
    group: 'Label dokumen cetak',
    label: 'TTD pembeli (keterangan)',
    type: 'text',
    def: 'Pemilik Toko',
    maxLength: 60,
  },
  {
    key: 'doc_title_rekap',
    group: 'Label dokumen cetak',
    label: 'Judul rekap muat',
    type: 'text',
    def: 'REKAPITULASI',
    maxLength: 60,
  },
  {
    key: 'doc_title_tagihan',
    group: 'Label dokumen cetak',
    label: 'Judul surat jalan tagihan',
    type: 'text',
    def: 'SURAT JALAN NOTA TAGIHAN',
    maxLength: 60,
  },
  {
    key: 'doc_title_spo',
    group: 'Label dokumen cetak',
    label: 'Judul cetak SPO',
    type: 'text',
    def: 'SURAT PESANAN (SPO)',
    maxLength: 60,
  },
  {
    key: 'doc_sign_spo',
    group: 'Label dokumen cetak',
    label: 'Kolom TTD cetak SPO',
    type: 'text',
    def: 'Penerima / Toko, Driver, Admin / Gudang',
    maxLength: 120,
    hint: 'Pisahkan dengan koma.',
  },
  {
    key: 'doc_title_dks',
    group: 'Label dokumen cetak',
    label: 'Judul cetak DKS',
    type: 'text',
    def: 'DAFTAR KUNJUNGAN SALESMAN',
    maxLength: 60,
  },
  {
    key: 'doc_sign_dks',
    group: 'Label dokumen cetak',
    label: 'Kolom TTD cetak DKS',
    type: 'text',
    def: 'Salesman, Supervisor, Diketahui',
    maxLength: 120,
    hint: 'Pisahkan dengan koma.',
  },
  {
    key: 'doc_sign_rekap',
    group: 'Label dokumen cetak',
    label: 'Kolom TTD rekap muat',
    type: 'text',
    def: 'Diperiksa, Diserahkan, Diketahui',
    maxLength: 120,
    hint: 'Pisahkan dengan koma.',
  },
  {
    key: 'doc_sign_tagihan',
    group: 'Label dokumen cetak',
    label: 'Kolom TTD surat jalan tagihan',
    type: 'text',
    def: 'Diterima, Diserahkan',
    maxLength: 120,
    hint: 'Pisahkan dengan koma.',
  },
  // ---- Kontak bantuan teknis (tampil di Panduan bila diisi) ----
  { key: 'support_name', group: 'Kontak bantuan teknis', label: 'Nama penyedia', type: 'text', def: '', maxLength: 120 },
  { key: 'support_phone', group: 'Kontak bantuan teknis', label: 'No. HP/WA', type: 'text', def: '', maxLength: 60 },
  { key: 'support_email', group: 'Kontak bantuan teknis', label: 'Email', type: 'text', def: '', maxLength: 120 },
  { key: 'support_hours', group: 'Kontak bantuan teknis', label: 'Jam layanan', type: 'text', def: '', maxLength: 120 },
];

export const SETTING_GROUPS: string[] = [...new Set(SETTINGS.map((s) => s.group))];

/** Tab di menu Pengaturan — tiap kelompok isian masuk tepat satu tab (diuji), disimpan per tab. */
export const SETTING_TABS: readonly { key: string; label: string; groups: readonly string[] }[] = [
  { key: 'usaha', label: 'Usaha', groups: ['Data usaha', 'Lokasi gudang / kantor'] },
  {
    key: 'aturan',
    label: 'Penjualan & aturan',
    groups: ['Penjualan', 'Aturan kunjungan sales', 'Ambang & perhitungan', 'Data & privasi'],
  },
  { key: 'dokumen', label: 'Dokumen & cetak', groups: ['Cetak nota', 'Label dokumen cetak', 'Penomoran dokumen'] },
  { key: 'bantuan', label: 'Kontak bantuan', groups: ['Kontak bantuan teknis'] },
];
export const SETTING_DEFAULTS: Record<string, string> = Object.fromEntries(SETTINGS.map((s) => [s.key, s.def]));
const BY_KEY = new Map(SETTINGS.map((s) => [s.key, s]));
export const settingDef = (k: string): SettingDef | undefined => BY_KEY.get(k);

/** Daftar dipisah koma dari satu pengaturan (mis. kolom tanda tangan). */
export function settingCsv(settings: Record<string, string | undefined>, key: string): string[] {
  return String(settings[key] ?? SETTING_DEFAULTS[key] ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Angka dari pengaturan (nilai awal katalog bila kosong/tidak valid). */
export function settingNum(settings: Record<string, string | undefined>, key: string): number {
  const v = Number(settings[key]);
  if (settings[key] !== undefined && settings[key] !== '' && Number.isFinite(v)) return v;
  return Number(SETTING_DEFAULTS[key]);
}

/** Validasi satu nilai; kembalikan pesan error atau null. */
export function validateSetting(key: string, value: unknown): string | null {
  const d = settingDef(key);
  if (!d) return null;
  const v = String(value ?? '');
  if (d.type === 'number') {
    if (v === '') return d.def === '' ? null : `${d.label} wajib diisi.`;
    const n = Number(v);
    if (!Number.isFinite(n) || (!d.decimal && !Number.isInteger(n))) return `${d.label} harus angka${d.decimal ? '' : ' bulat'}.`;
    if ((d.min !== undefined && n < d.min) || (d.max !== undefined && n > d.max)) return `${d.label} harus ${d.min}–${d.max}.`;
  }
  if (d.pattern && v && !new RegExp(d.pattern).test(v)) return `${d.label}: ${d.patternMsg ?? 'format tidak sesuai'}.`;
  if (d.type === 'select' && !(d.options ?? []).some((o) => o.value === v)) return `${d.label} tidak valid.`;
  if (d.maxLength && v.length > d.maxLength) return `${d.label} maksimal ${d.maxLength} karakter.`;
  return null;
}

/** Validasi gabungan antar-kunci (dipanggil setelah validateSetting per kunci). */
export function validateSettingsAll(all: Record<string, string | undefined>): string | null {
  if (settingNum(all, 'aging_days_2') <= settingNum(all, 'aging_days_1'))
    return 'Batas kedua umur piutang harus lebih besar dari batas pertama.';
  const pfx = SETTINGS.filter((d) => d.key.startsWith('prefix_') && d.key !== 'prefix_store').map((d) => all[d.key] ?? d.def);
  if (new Set(pfx).size !== pfx.length) return 'Awalan nomor dokumen tidak boleh sama.';
  return null;
}
